'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  Search,
  ShoppingCart,
  X,
  Clock,
  Filter,
  ChevronDown,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ProductCard } from '@/components/products/ProductCard';
import { ProductDrawer } from '@/components/products/ProductDrawer';
import { useCart } from '@/hooks/useCart';
import { CartProvider } from '@/hooks/useCart';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useUser } from '../../contexts/UserContext';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CartDrawer } from './CartDrawer';

function SearchPageContent() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchHistory, setSearchHistory] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [popularSearches] = useState(['Vegetables', 'Fruits', 'Dairy', 'Organic', 'Fresh']);
  const [recommendations, setRecommendations] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
  const [sortOption, setSortOption] = useState('relevance');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [productCategoryMap, setProductCategoryMap] = useState({});

  const searchInputRef = useRef(null);
  const navigate = useNavigate();
  const { cart, addToCart } = useCart();
  const { logout } = useAuth();
  const { user, refreshUser } = useUser();

  const sessionToken = useMemo(() => localStorage.getItem('sessionToken'), []);
  const refreshToken = useMemo(() => localStorage.getItem('refreshToken'), []);

  const headers = useMemo(
    () => ({
      sessionToken,
      refreshToken,
    }),
    [sessionToken, refreshToken]
  );

  useEffect(() => {
    const history = JSON.parse(localStorage.getItem('searchHistory') || '[]');
    setSearchHistory(history);
    if (user) {
      fetchCategories();
      // Load search history from localStorage
    } else {
      refreshUser();
    }
  }, [user]);

  useEffect(() => {
    if (categories.length > 0 && user) {
      fetchAllProducts();
    }
  }, [categories, user]);

  useEffect(() => {
    if (allProducts.length > 0) {
      filterProducts();
      generateRecommendations();
    }
  }, [searchTerm, allProducts, selectedCategories, activeTab, sortOption, priceRange]);

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const { data } = await axios.get(
        `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/products/categories`,
        {
          headers,
        }
      );

      let vendorCategories;
      if (user?.userType === 'vendor') {
        vendorCategories = user?.vendorCategories || [];
      } else {
        const selectedVendor = JSON.parse(localStorage.getItem('selectedVendor'));
        vendorCategories = selectedVendor?.vendorCategories || [];
      }

      const syncedCategories = data.filter((category) => vendorCategories.includes(category.name));

      setCategories(syncedCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchAllProducts = async () => {
    try {
      setLoading(true);
      const selectedVendorData = JSON.parse(localStorage.getItem('selectedVendor'));

      if (user?.userType === 'user' && !selectedVendorData) {
        return;
      }

      let vendorId;
      if (user?.userType === 'user') {
        vendorId = selectedVendorData.vendorId;
      } else {
        vendorId = user?.id;
      }

      // Skip if no categories
      if (!categories || categories.length === 0) {
        setLoading(false);
        return;
      }

      // Create a map to store which category each product belongs to
      const categoryMap = {};

      const productPromises = categories.map((category) =>
        axios
          .get(
            `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/products?category=${category.id}`,
            {
              headers: {
                'X-Vendor-Id': vendorId,
                sessionToken: localStorage.getItem('sessionToken'),
                refreshToken: localStorage.getItem('refreshToken'),
              },
            }
          )
          .then((response) => {
            // For each product in this category, add an entry to the map
            response.data.forEach((product) => {
              categoryMap[product.productId] = category.id;
            });
            return response;
          })
      );

      // Use Promise.allSettled instead of Promise.all to handle individual failures
      const results = await Promise.allSettled(productPromises);

      // Filter for successful responses only
      const successfulResponses = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value.data);

      // Flatten the array of product arrays
      const products = successfulResponses.flat();

      // Log any failed requests
      const failedRequests = results.filter((result) => result.status === 'rejected');
      if (failedRequests.length > 0) {
        console.warn(
          `${failedRequests.length} category requests failed, but continuing with available products`
        );
      }

      // Store the category map
      setProductCategoryMap(categoryMap);
      setAllProducts(products);
    } catch (error) {
      console.error('Error in fetchAllProducts:', error);
      // Only logout on specific auth errors
      if (
        error.status === 401 ||
        error?.response?.data === 'Unauthorized: Invalid or missing token.'
      ) {
        logout();
      } else {
        // For other errors, just show a toast but continue
        toast.error('Some products could not be loaded');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateRecommendations = () => {
    // In a real app, this would be based on user behavior, purchase history, etc.
    // For now, we'll just pick some random products as recommendations
    if (allProducts.length === 0) return;

    const randomProducts = [...allProducts].sort(() => 0.5 - Math.random()).slice(0, 6);

    setRecommendations(randomProducts);
  };

  const filterProducts = () => {
    if (allProducts.length === 0) return;

    let filtered = [...allProducts];

    // Filter by search term
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter((product) => {
        const categoryId = productCategoryMap[product.productId];
        const category = categories.find((cat) => cat.id === categoryId);
        const categoryName = category ? category.name : '';

        return (
          product.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (categoryName && categoryName.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      });
    }

    // Filter by selected categories
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((product) => {
        const categoryId = productCategoryMap[product.productId];
        return selectedCategories.includes(categoryId);
      });
    }

    // Filter by tab (category group)
    if (activeTab !== 'all') {
      // This is a simplified example - in a real app, you'd have proper category grouping
      filtered = filtered.filter((product) => {
        const categoryId = productCategoryMap[product.productId];
        const category = categories.find((cat) => cat.id === categoryId);
        const categoryName = category ? category.name : '';

        if (activeTab === 'vegetables') {
          return categoryName === 'Vegetables' || categoryName === 'Leafy Vegetables';
        } else if (activeTab === 'fruits') {
          return categoryName === 'Fruits' || categoryName === 'Seasonal Fruits';
        } else if (activeTab === 'dairy') {
          return categoryName === 'Dairy' || categoryName === 'Milk Products';
        }
        return false;
      });
    }

    // Filter by price range
    filtered = filtered.filter((product) => {
      const price = product.netPrice || 0;
      return price >= priceRange.min && price <= priceRange.max;
    });

    // Sort products
    if (sortOption === 'price-low-high') {
      filtered.sort((a, b) => (a.netPrice || 0) - (b.netPrice || 0));
    } else if (sortOption === 'price-high-low') {
      filtered.sort((a, b) => (b.netPrice || 0) - (a.netPrice || 0));
    } else if (sortOption === 'name-a-z') {
      filtered.sort((a, b) => a.productName.localeCompare(b.productName));
    } else if (sortOption === 'name-z-a') {
      filtered.sort((a, b) => b.productName.localeCompare(a.productName));
    }

    setFilteredProducts(filtered);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;

    // Add to search history
    const newHistory = [searchTerm, ...searchHistory.filter((item) => item !== searchTerm)].slice(
      0,
      5
    );
    setSearchHistory(newHistory);
    localStorage.setItem('searchHistory', JSON.stringify(newHistory));

    setShowSuggestions(false);
    filterProducts();
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    setShowSuggestions(false);

    // Add to search history
    const newHistory = [suggestion, ...searchHistory.filter((item) => item !== suggestion)].slice(
      0,
      5
    );
    setSearchHistory(newHistory);
    localStorage.setItem('searchHistory', JSON.stringify(newHistory));

    filterProducts();
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    searchInputRef.current?.focus();
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((c) => c !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setIsDrawerOpen(true);
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setPriceRange({ min: 0, max: 1000 });
    setSortOption('relevance');
    setActiveTab('all');
  };

  const getCategoryNameById = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  const getCategoryForProduct = (product) => {
    const categoryId = productCategoryMap[product.productId];
    return getCategoryNameById(categoryId);
  };

  const renderProductGrid = () => {
    if (loading || loadingCategories) {
      return (
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-lg overflow-hidden border">
              <Skeleton className="h-32 w-full" />
              <div className="p-3 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (filteredProducts.length === 0) {
      return (
        <div className="text-center mt-8 py-10">
          <div className="bg-gray-100 inline-flex rounded-full p-3 mb-4">
            <Search className="h-6 w-6 text-gray-400" />
          </div>
          <p className="text-gray-700 font-medium">No products found</p>
          <p className="text-sm text-gray-500 mt-2">Try adjusting your search or filters</p>
          {selectedCategories.length > 0 || activeTab !== 'all' || searchTerm ? (
            <Button variant="outline" className="mt-4" onClick={handleClearFilters}>
              Clear all filters
            </Button>
          ) : null}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 gap-4">
        {filteredProducts.map((product) => (
          <ProductCard
            key={product.productId}
            product={product}
            onSelect={handleProductSelect}
            category={getCategoryForProduct(product)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 sm:border-l sm:border-r">
      <div className="max-w-md mx-auto">
        <header className="flex items-center gap-4 bg-white border-b px-4 py-3 sticky top-0 z-20">
          <button className="p-1" onClick={() => navigate(-1)} aria-label="Go back">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <form onSubmit={handleSearch} className="flex-1 relative">
            <div className="flex items-center relative">
              <Search className="h-4 w-4 absolute left-3 text-gray-400" />
              <Input
                ref={searchInputRef}
                type="search"
                placeholder="Search products, categories..."
                value={searchTerm}
                autoFocus
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 pl-9 pr-8 focus-visible:ring-offset-0 focus-visible:ring-1"
                onFocus={() => searchTerm.length > 1 && setShowSuggestions(true)}
              />
              {searchTerm && (
                <button type="button" className="absolute right-3" onClick={handleClearSearch}>
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>

            {/* Search suggestions dropdown */}
          </form>

          <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="relative">
                <Filter className="h-5 w-5" />
                {selectedCategories.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {selectedCategories.length}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[350px]">
              <SheetHeader>
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <div className="py-4">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium mb-2">Categories</h3>
                    <div className="space-y-2">
                      {categories.map((category) => (
                        <div key={category.id} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`category-${category.id}`}
                            checked={selectedCategories.includes(category.id)}
                            onChange={() => handleCategoryToggle(category.id)}
                            className="mr-2"
                          />
                          <label htmlFor={`category-${category.id}`} className="text-sm">
                            {category.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span>₹{priceRange.min}</span>
                    <span>₹{priceRange.max}</span>
                  </div>
                  <div className="flex gap-4">
                    <Input
                      type="number"
                      placeholder="Min"
                      value={priceRange.min}
                      onChange={(e) =>
                        setPriceRange((prev) => ({ ...prev, min: Number(e.target.value) }))
                      }
                      className="w-1/2"
                    />
                    <Input
                      type="number"
                      placeholder="Max"
                      value={priceRange.max}
                      onChange={(e) =>
                        setPriceRange((prev) => ({ ...prev, max: Number(e.target.value) }))
                      }
                      className="w-1/2"
                    />
                  </div>
                </div>

                <div className="flex gap-2 mt-6">
                  <Button variant="outline" className="flex-1" onClick={handleClearFilters}>
                    Clear All
                  </Button>
                  <SheetClose asChild>
                    <Button className="flex-1">Apply</Button>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </header>

        {/* Active filters */}
        {selectedCategories.length > 0 && (
          <div className="bg-white px-4 py-2 border-b overflow-x-auto whitespace-nowrap flex gap-2">
            {selectedCategories.map((categoryId) => (
              <Badge key={categoryId} variant="secondary" className="flex items-center gap-1">
                {getCategoryNameById(categoryId)}
                <button onClick={() => handleCategoryToggle(categoryId)}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => setSelectedCategories([])}
            >
              Clear all
            </Button>
          </div>
        )}

        {/* Search history and popular searches */}
        {searchHistory.length > 0 && (
          <div className="bg-white px-4 py-1 border-b">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700">Recent Searches</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-gray-500"
                  onClick={() => {
                    setSearchHistory([]);
                    localStorage.removeItem('searchHistory');
                  }}
                >
                  Clear
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {searchHistory.map((term, index) => (
                  <button
                    key={index}
                    className="flex items-center gap-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1"
                    onClick={() => handleSuggestionClick(term)}
                  >
                    <Clock className="h-3 w-3" />
                    {term}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <main className="mx-auto px-4 py-4">
          {/* Sort options */}
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">
              {filteredProducts.length} {filteredProducts.length === 1 ? 'result' : 'results'}
            </p>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs flex items-center gap-1">
                  Sort by
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-0">
                <div className="py-1">
                  <button
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortOption === 'relevance' ? 'bg-gray-50 font-medium' : ''}`}
                    onClick={() => setSortOption('relevance')}
                  >
                    Relevance
                  </button>
                  <button
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortOption === 'price-low-high' ? 'bg-gray-50 font-medium' : ''}`}
                    onClick={() => setSortOption('price-low-high')}
                  >
                    Price: Low to High
                  </button>
                  <button
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortOption === 'price-high-low' ? 'bg-gray-50 font-medium' : ''}`}
                    onClick={() => setSortOption('price-high-low')}
                  >
                    Price: High to Low
                  </button>
                  <button
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortOption === 'name-a-z' ? 'bg-gray-50 font-medium' : ''}`}
                    onClick={() => setSortOption('name-a-z')}
                  >
                    Name: A to Z
                  </button>
                  <button
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortOption === 'name-z-a' ? 'bg-gray-50 font-medium' : ''}`}
                    onClick={() => setSortOption('name-z-a')}
                  >
                    Name: Z to A
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Recommendations section */}
          {!searchTerm && !loading && recommendations.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-medium flex items-center gap-1">
                  <Sparkles className="h-4 w-4 text-yellow-500" />
                  Recommended for you
                </h2>
                <Button
                  variant="link"
                  size="sm"
                  className="text-xs"
                  onClick={() => navigate('/store')}
                >
                  See all
                  <ChevronRight className="h-3 w-3 ml-1" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {recommendations.slice(0, 4).map((product) => (
                  <ProductCard
                    key={product.productId}
                    product={product}
                    onSelect={handleProductSelect}
                    category={getCategoryForProduct(product)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Main product grid */}
          {renderProductGrid()}
        </main>

        {user?.userType === 'user' && cart.length > 0 && (
          <div className="fixed max-w-md mx-auto bottom-5 left-4 right-4">
            <Button
              className="w-full mx-auto flex bg-[#39c55e] hover:bg-[#2ea34d] text-white shadow-lg transition-all duration-200"
              onClick={() => navigate('/cart')}
            >
              <ShoppingCart className="mr-2 h-5 w-5" />
              {cart?.length} {cart?.length === 1 ? 'item' : 'items'} | Go to Cart
            </Button>
          </div>
        )}

        {!['Grocery', 'Extras'].includes(
          selectedProduct ? getCategoryForProduct(selectedProduct) : ''
        ) && (
          <ProductDrawer
            product={selectedProduct}
            isOpen={isDrawerOpen}
            onClose={() => {
              setIsDrawerOpen(false);
              setSelectedProduct(null);
            }}
            category={selectedProduct ? getCategoryForProduct(selectedProduct) : ''}
          />
        )}
        {['Grocery', 'Extras'].includes(
          selectedProduct ? getCategoryForProduct(selectedProduct) : ''
        ) && (
          <CartDrawer
            product={selectedProduct}
            isOpen={isDrawerOpen}
            onClose={() => {
              setIsDrawerOpen(false);
              setSelectedProduct(null);
            }}
            isCartBlocked={false}
            category={selectedProduct ? getCategoryForProduct(selectedProduct) : ''}
          />
        )}
      </div>
    </div>
  );
}

export default function SearchPage() {
  return (
    <CartProvider>
      <SearchPageContent />
    </CartProvider>
  );
}
